import { BookingType } from "../../../../../../features/Booking-Form/typings/booking-inputs";
import type { NotSerializedBookingService } from "../../../../../../features/Booking-Form/typings/booking-inputs";
import { BookingInputs } from "../../../../Domain/Types/BookingInputs.type";
import { IFloorPayload } from "../../../../Domain/Types/FloorPlan/FloorPayload.type";
import { IPlaceSchedule } from "../../../../Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneSchedule } from "../../../../Domain/Types/FloorPlan/ZoneSchedule";
import {
  areMandatoryConferenceServicesFulfilled,
  hasConferenceServiceOrderSelection,
  hasOrderableServiceInMandatoryCategories,
  hasServicePlanMandatory,
  isMandatoryConferenceServiceIncomplete,
  isOrderableServiceExist
} from "../BookingServicePrompt/functions/hasMandatory.function";

export type SelectionPrompt2SelectedEntry = IPlaceSchedule | IZoneSchedule;

export function computeSelectionPrompt2Derived(params: {
  bookingInputs: BookingInputs;
  floorPlanForService: IFloorPayload | undefined;
  selectedZone: number | undefined;
  bookingFrom: string | null;
  bookingStart: string | null;
  maxSetupTime: number;
  conferenceService: NotSerializedBookingService | undefined;
  selectedEntry: SelectionPrompt2SelectedEntry;
  selectableByVariant: boolean;
  isServiceAdminBypass: boolean;
}) {
  const {
    bookingInputs,
    floorPlanForService,
    selectedZone,
    bookingFrom,
    bookingStart,
    maxSetupTime,
    conferenceService,
    selectedEntry,
    selectableByVariant,
    isServiceAdminBypass
  } = params;

  const isConferenceZone = bookingInputs.bookingType === BookingType.CONFERENCEZONE;
  const hasMandatory = hasServicePlanMandatory({ floorPlanForService, selectedZone });
  const hasAnyOrderableService = isOrderableServiceExist({
    floorPlanForService,
    selectedZone,
    bookingFrom,
    bookingStart,
    maxSetupTime,
    isServiceAdminBypass
  });
  const hasOrderableService =
    isServiceAdminBypass || !hasMandatory
      ? hasAnyOrderableService
      : hasOrderableServiceInMandatoryCategories({
          floorPlanForService,
          selectedZone,
          bookingFrom,
          bookingStart,
          maxSetupTime
        });
  const conferenceServiceInventoryKey = selectedEntry?.inventory?.id || selectedEntry?.inventoryId;
  const orderedConferenceService =
    conferenceServiceInventoryKey == null
      ? undefined
      : conferenceService?.[conferenceServiceInventoryKey];

  const hasExistingConferenceServiceOrder =
    hasConferenceServiceOrderSelection(orderedConferenceService);
  /** Opening the services UI only makes sense when something is orderable or already ordered; mandatory-on-plan alone does not imply any line item passes lead/setup-time rules. */
  const isConferenceServicesActionEnabled =
    hasOrderableService || hasExistingConferenceServiceOrder;
  const mandatoryServicesFulfilled = areMandatoryConferenceServicesFulfilled({
    floorPlanForService,
    selectedZone,
    orderedService: orderedConferenceService
  });
  const mandatoryConferenceServiceIncomplete = isMandatoryConferenceServiceIncomplete({
    isConferenceZoneBooking: isConferenceZone,
    floorPlanForService,
    selectedZone,
    conferenceServiceInventoryKey,
    conferenceService
  });
  const disableConfirmBooking =
    !selectableByVariant || (!isServiceAdminBypass && mandatoryConferenceServiceIncomplete);

  return {
    isConferenceZone,
    hasMandatory,
    hasOrderableService,
    hasExistingConferenceServiceOrder,
    isConferenceServicesActionEnabled,
    conferenceServiceInventoryKey,
    mandatoryServicesFulfilled,
    mandatoryConferenceServiceIncomplete,
    disableConfirmBooking
  };
}
